'use client';

import React, { useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import ErrorCodeEnum from '@/enums/error-code-enum';
import { confirmWithdrawal } from '@/server-functions/withdrawal/withdrawal-actions';
import {
  ConfirmWithdrawalResult,
  WithdrawalSource,
} from '@/server-functions/withdrawal/withdrawal-types';
import Button from '@/ui/components/shared/Button';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { buildWithdrawalSuccessSearchParams } from '../withdrawal-stage';
import {
  WithdrawalConfirmDialog,
  WithdrawalConfirmDialogTexts,
} from '../withdrawal-confirm-dialog/withdrawal-confirm-dialog';

type WithdrawalConfirmButtonProps = Readonly<{
  token: string;
  source: WithdrawalSource;
  buttonText?: string | null;
  dialogTexts: WithdrawalConfirmDialogTexts;
  onError: (errorCode?: ErrorCodeEnum) => void;
  trackingId?: string;
}>;

export function WithdrawalConfirmButton({
  token,
  source,
  buttonText,
  dialogTexts,
  onError,
  trackingId,
}: WithdrawalConfirmButtonProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmingRef = useRef(false);

  const handleOpen = () => {
    // The public flow already pushed step 2 on the request form.
    if (source === 'myaccount') {
      DataLayerHelper.pushWithdrawalStep(2, 'myaccount');
    }
    setIsDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (confirmingRef.current) {
      return;
    }
    confirmingRef.current = true;
    setIsConfirming(true);
    setIsDialogOpen(false);
    DataLayerHelper.pushWithdrawalStep(3, source);

    let result: ConfirmWithdrawalResult;
    try {
      result = await confirmWithdrawal(token);
    } catch {
      result = { status: 'error' };
    }

    if (result.status === 'ok') {
      // Keeps the button disabled until the success stage replaces the confirm stage.
      const params = buildWithdrawalSuccessSearchParams(result.outcome);
      router.replace(`${pathname}?${params.toString()}`);
      return;
    }

    confirmingRef.current = false;
    setIsConfirming(false);
    onError(result.errorCode);
  };

  return (
    <>
      <Button
        className="w-full sm:w-auto"
        onClick={handleOpen}
        disabled={isConfirming}
        loading={isConfirming}
        data-tile-element-id={trackingId}
      >
        {buttonText}
      </Button>
      <WithdrawalConfirmDialog
        open={isDialogOpen}
        texts={dialogTexts}
        onConfirm={handleConfirm}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
}
